/**
 * Cross-page handoff for a grass photo picked OUTSIDE the homepage (blog inline
 * upload CTA). A File cannot ride a URL or sessionStorage, but the Next App
 * Router does CLIENT-SIDE navigation between blog and home, so module state
 * survives the trip. On hard reload the module resets and the user simply lands
 * on the normal uploader (graceful degradation — measured via the gap between
 * cta_clicked[file_selected] and grass_id_photo_uploaded[entry_source=blog_*]).
 *
 * The stash is consume-once and TTL-bounded so a stale photo can never
 * silently attach to a later, unrelated visit to the homepage.
 */

export interface PendingScanFile {
  file: File
  /** analytics entry_source, e.g. 'blog_inline_upload' */
  source: string
  stashedAt: number
}

const TTL_MS = 60_000

let pending: PendingScanFile | null = null
let expiryTimer: ReturnType<typeof setTimeout> | null = null

export function stashPendingScanFile(file: File, source: string): void {
  pending = { file, source, stashedAt: Date.now() }
  // Expire proactively, not just at consumption: an abandoned navigation must
  // not pin a multi-MB (and potentially sensitive) photo in memory for the
  // rest of the tab session.
  if (expiryTimer) clearTimeout(expiryTimer)
  expiryTimer = setTimeout(() => {
    pending = null
    expiryTimer = null
  }, TTL_MS)
}

/** Returns the stashed file once (clears on read); null if absent or expired. */
export function consumePendingScanFile(): PendingScanFile | null {
  const current = pending
  pending = null
  if (expiryTimer) {
    clearTimeout(expiryTimer)
    expiryTimer = null
  }
  if (!current) return null
  if (Date.now() - current.stashedAt > TTL_MS) return null
  return current
}
